import { IconBonus, IconCheck, IconClock, IconTransfer, IconWalletCoin } from '@holbonkit/icons'
import { Box, Text } from '@holbonkit/ui'
import clsx from 'clsx'
import dayjs from 'dayjs'
import type { ReactNode } from 'react'

import type {
    SelfEmployedTransactionListItemStatusEnumKey,
    SelfEmployedTransactionListItemTypeEnumKey,
} from '@/shared/api'
import { currencyParser } from '@/shared/lib/currency-parser'
import { Icon } from '@/shared/ui'

import styles from './operation-row.module.scss'

type TIconTone = 'blue' | 'gray' | 'green' | 'purple' | 'yellow'
type TAmountTone = 'negative' | 'positive'

type TOperationView = {
    amountPrefix?: '+' | '-'
    amountTone?: TAmountTone
    caption: string
    icon: ReactNode
    iconTone: TIconTone
    title: string
}

const iconToneClass: Record<TIconTone, string> = {
    blue: styles['operation-row__icon--blue'],
    gray: styles['operation-row__icon--gray'],
    green: styles['operation-row__icon--green'],
    purple: styles['operation-row__icon--purple'],
    yellow: styles['operation-row__icon--yellow'],
}

const amountToneClass: Record<TAmountTone, string> = {
    negative: styles['operation-row__info--item--negative'],
    positive: styles['operation-row__info--item--positive'],
}

const carIcon = <Icon color='var(--text-and-icon-accent)' icon='car' size={24} />
const clockWarningIcon = (
    <IconClock color='var(--statement-text-and-icon-warning-pressed)' size={24} />
)
const clockIcon = <IconClock color='var(--text-and-icon-secondary)' size={24} />
const checkIcon = <IconCheck color='var(--extensions-text-and-icon-success)' size={24} />
const transferIcon = <IconTransfer color='var(--text-and-icon-secondary)' size={24} />
const walletIcon = <IconWalletCoin color='#7A74C9' size={24} />
const bonusIcon = <IconBonus color='#7A74C9' size={24} />

const operationViews: Record<SelfEmployedTransactionListItemTypeEnumKey, TOperationView> = {
    order: {
        title: 'Доход за заказ',
        caption: 'Начислен',
        icon: carIcon,
        iconTone: 'blue',
        amountPrefix: '+',
        amountTone: 'positive',
    },
    payout: {
        title: 'Вывод средств',
        caption: 'Выполнен',
        icon: checkIcon,
        iconTone: 'green',
        amountPrefix: '-',
    },
    compensation: {
        title: 'Компенсация за подачу',
        caption: 'Начислена',
        icon: carIcon,
        iconTone: 'blue',
        amountPrefix: '+',
        amountTone: 'positive',
    },
    refund: {
        title: 'Возврат пассажиру',
        caption: 'Оформлен',
        icon: transferIcon,
        iconTone: 'gray',
    },
    dotaion: {
        title: 'Выплата за заказ',
        caption: 'Начислена',
        icon: walletIcon,
        iconTone: 'purple',
        amountPrefix: '+',
        amountTone: 'positive',
    },
    achivment: {
        title: 'Награда за цель',
        caption: 'Начислена',
        icon: bonusIcon,
        iconTone: 'purple',
        amountPrefix: '+',
        amountTone: 'positive',
    },
}

const statusViews: Partial<
    Record<
        SelfEmployedTransactionListItemTypeEnumKey,
        Partial<Record<SelfEmployedTransactionListItemStatusEnumKey, Partial<TOperationView>>>
    >
> = {
    order: {
        waiting: {
            caption: 'В обработке',
            icon: clockWarningIcon,
            iconTone: 'yellow',
            amountPrefix: undefined,
            amountTone: undefined,
        },
        canceled: {
            caption: 'Операция отменена',
            icon: clockIcon,
            iconTone: 'gray',
            amountPrefix: undefined,
            amountTone: 'negative',
        },
    },
    payout: {
        waiting: {
            caption: 'В обработке',
            icon: transferIcon,
            iconTone: 'gray',
            amountPrefix: undefined,
            amountTone: undefined,
        },
        canceled: {
            caption: 'Отклонён',
            icon: transferIcon,
            iconTone: 'gray',
            amountPrefix: undefined,
            amountTone: 'negative',
        },
    },
}

const getOperationView = (
    type: SelfEmployedTransactionListItemTypeEnumKey,
    status?: SelfEmployedTransactionListItemStatusEnumKey,
): null | TOperationView => {
    const view = operationViews[type]
    const statusView = status ? statusViews[type]?.[status] : undefined

    if ((type === 'order' || type === 'payout') && status !== 'success' && !statusView) {
        return null
    }

    return { ...view, ...statusView }
}

const formatOperationDate = (date: string) => {
    const value = dayjs(date)

    return value.isSame(dayjs(), 'day') ? value.format('HH:mm') : value.format('DD.MM.YY HH:mm')
}

export const OperationRow = ({
    amount,
    date,
    status,
    type,
}: {
    amount: number
    date: string
    status?: SelfEmployedTransactionListItemStatusEnumKey
    type: SelfEmployedTransactionListItemTypeEnumKey
}) => {
    const view = getOperationView(type, status)

    if (!view) {
        return null
    }

    return (
        <Box className={styles['operation-row']}>
            <Box className={clsx(styles['operation-row__icon'], iconToneClass[view.iconTone])}>
                {view.icon}
            </Box>
            <Box className={styles['operation-row__info']}>
                <Box className={styles['operation-row__info--item']}>
                    <Text variant='body-m-16-medium'>{view.title}</Text>
                    <Text
                        className={view.amountTone ? amountToneClass[view.amountTone] : undefined}
                        variant='body-m-16-medium'
                    >
                        {view.amountPrefix}
                        {currencyParser(amount)} ₽
                    </Text>
                </Box>
                <Box className={styles['operation-row__info--item']}>
                    <Text
                        className={styles['operation-row__info--item--subtitle']}
                        variant='body-s-14-regular'
                    >
                        {view.caption}
                    </Text>
                    <Text
                        className={styles['operation-row__info--item--subtitle']}
                        variant='body-s-14-regular'
                    >
                        {formatOperationDate(date)}
                    </Text>
                </Box>
            </Box>
        </Box>
    )
}
